// AllergenManager.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../utils/axios';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Check,
  X,
  Eye,
  EyeOff,
  AlertCircle,
  RefreshCw,
  Upload,
  Globe,
  User
} from 'lucide-react';
import { getPublicAllergenIconPath, PUBLIC_ALLERGENS } from '../../utils/publicAllergens';
import { getAllergenDisplayNameItalian, getAllergenDescriptionItalian } from '../../utils/allergenDisplayNames';

// Resolve icon URL/path for display: prefer saved SVG by code (A–N), then stored icon (URL/path), else emoji/text
const getAllergenIconForDisplay = (allergen) => {
  const byCode = getPublicAllergenIconPath(allergen?.code);
  if (byCode) return byCode;
  const icon = allergen?.icon;
  if (icon && (icon.startsWith('http') || icon.startsWith('/') || icon.includes('cloudinary'))) return icon;
  return icon || null;
};

const isIconImage = (icon) => icon && (icon.startsWith('http') || icon.startsWith('/') || icon.includes('cloudinary'));

const AllergenManager = ({ menuId, allergens: initialAllergens = [], onAllergensUpdate }) => {
  const { t } = useTranslation();
  const [allergens, setAllergens] = useState(initialAllergens);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [error, setError] = useState(null);
  
  // Form states
  const [showForm, setShowForm] = useState(false);
  const [editingAllergen, setEditingAllergen] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    icon: '',
    iconFile: null,
    iconUrl: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Load allergens on component mount
  useEffect(() => {
    fetchAllergens();
  }, []);

 const fetchAllergens = useCallback(async () => {
  if (!menuId) {
    toast.error(t('allergen_manager.menu_id_required'));
    return;
  }

  try {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('token');
    
    // Use the existing endpoint with menuId as query parameter
    const response = await axios.get(`/allergens?menuId=${menuId}`, {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.data.success) {
      const allergensData = response.data.data || [];
      setAllergens(allergensData);
      
      if (onAllergensUpdate) {
        onAllergensUpdate(allergensData);
      }
    }
  } catch (error) {
    console.error('Failed to fetch allergens:', error);
    setError(t('allergen_manager.messages.error_load'));
    toast.error(t('allergen_manager.messages.error_load'));
  } finally {
    setLoading(false);
  }
}, [menuId, onAllergensUpdate, t]);

  // Filter allergens based on search and active status
  const filteredAllergens = allergens.filter(allergen => {
    const matchesSearch = !searchTerm || 
      allergen.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      allergen.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (allergen.description && allergen.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = showInactive || allergen.isActive;
    
    return matchesSearch && matchesStatus;
  });

 // Add EU Standard Allergens - UPDATED to send menuId
const addEUAllergens = async () => {
  if (!menuId) {
    toast.error('Menu ID is required to add allergens');
    return;
  }

  if (window.confirm(t('allergen_manager.eu_add_confirm'))) {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Send menuId in the request body
      const response = await axios.post('/allergens/eu-allergens', 
        { menuId: menuId }, // Send menuId in request body
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        const createdCount = response.data.data?.length || 0;
        const skippedCount = response.data.skipped?.length || 0;
        
        let message = response.data.message;
        
        if (createdCount > 0) {
          message = t('allergen_manager.success_eu_added_count', { count: createdCount });
          if (skippedCount > 0) {
            message += ' ' + t('allergen_manager.success_eu_skipped', { count: skippedCount });
          }
        } else {
          message = t('allergen_manager.success_eu_all_exist');
        }
        
        toast.success(message);
        fetchAllergens(); // Refresh the list
      }
    } catch (error) {
      console.error('Failed to add EU allergens:', error);
      const errorMsg = error.response?.data?.error || t('allergen_manager.error_add_eu');
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }
};

 // Create Custom Allergen - Frontend update
const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!menuId) {
    toast.error(t('allergen_manager.menu_id_required_create'));
    return;
  }
  
  // Validation
  const errors = {};
  if (!formData.name.trim()) errors.name = t('allergen_manager.validation.name_required');
  if (!formData.code.trim()) errors.code = t('allergen_manager.validation.code_required');
  if (formData.code.length > 10) errors.code = t('allergen_manager.validation.code_max_length');
  
  if (Object.keys(errors).length > 0) {
    setFormErrors(errors);
    return;
  }
  
  try {
    setSaving(true);
    const token = localStorage.getItem('token');
    
    // Prepare form data - include menuId
    const submitData = new FormData();
    submitData.append('name', formData.name.trim());
    submitData.append('code', formData.code.trim().toUpperCase());
    submitData.append('description', formData.description || '');
    submitData.append('menuId', menuId); // Send menuId - IMPORTANT!
    
    if (formData.iconFile) {
      submitData.append('iconFile', formData.iconFile);
    } else if (formData.icon) {
      submitData.append('icon', formData.icon);
    }
    
    console.log('Submitting allergen with menuId:', menuId);
    
    const url = editingAllergen ? `/allergens/${editingAllergen.id}` : '/allergens';
    const method = editingAllergen ? 'put' : 'post';
    
    const response = await axios[method](url, submitData, {
      headers: { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });
    
    if (response.data.success) {
      const message = editingAllergen 
        ? t('allergen_manager.messages.success_update')
        : t('allergen_manager.messages.success_create');
      
      toast.success(message);
      
      // Check if menuId was saved
      console.log('Created allergen response:', response.data.data);
      console.log('menuId in response:', response.data.data?.menuId);
      
      resetForm();
      fetchAllergens();
    }
  } catch (error) {
    console.error('Save allergen error:', error);
    console.error('Error response:', error.response?.data);
    const errorMsg = error.response?.data?.error || t('allergen_manager.messages.error_operation');
    toast.error(errorMsg);
  } finally {
    setSaving(false);
  }
};

  // Edit Allergen
  const handleEdit = (allergen) => {
    setEditingAllergen(allergen);
    
    const isUploadedIcon = allergen.icon && (allergen.icon.startsWith('http') || allergen.icon.includes('cloudinary.com'));
    
    setFormData({
      name: allergen.name,
      code: allergen.code,
      description: allergen.description || '',
      icon: isUploadedIcon ? '' : (allergen.icon || ''),
      iconFile: null,
      iconUrl: isUploadedIcon ? allergen.icon : ''
    });
    setShowForm(true);
  };

  // Deactivate Allergen
  const handleDeactivate = async (allergen) => {
    if (!window.confirm(t('allergen_manager.deactivate_confirm', { name: allergen.name }))) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`/allergens/${allergen.id}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        toast.success(t('allergen_manager.success_deactivate'));
        fetchAllergens(); // Refresh list
      }
    } catch (error) {
      console.error('Deactivate allergen error:', error);
      const errorMsg = error.response?.data?.error || t('allergen_manager.error_deactivate');
      toast.error(errorMsg);
    }
  };

  // Reactivate Allergen
  const handleReactivate = async (allergen) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`/allergens/${allergen.id}/reactivate`, {}, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        toast.success(t('allergen_manager.success_reactivate'));
        fetchAllergens();
      }
    } catch (error) {
      console.error('Reactivate allergen error:', error);
      toast.error(t('allergen_manager.error_reactivate'));
    }
  };

  // Permanently Delete Allergen
  const handlePermanentDelete = async (allergen) => {
    const confirmMessage = allergen.isActive 
      ? t('allergen_manager.delete_permanent_warning', { name: allergen.name })
      : t('allergen_manager.delete_permanent_confirm', { name: allergen.name });
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`/allergens/${allergen.id}/permanent`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        toast.success(t('allergen_manager.success_permanent_delete'));
        fetchAllergens();
      }
    } catch (error) {
      console.error('Permanent delete error:', error);
      const errorMsg = error.response?.data?.error || t('allergen_manager.error_permanent_delete');
      toast.error(errorMsg);
    }
  };

  // Reset Form
  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      icon: '',
      iconFile: null,
      iconUrl: ''
    });
    setFormErrors({});
    setEditingAllergen(null);
    setShowForm(false);
  };

  // Handle Input Change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle Icon Upload
  const handleIconUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        toast.error(t('allergen_manager.validation.invalid_file_type'));
        return;
      }
      
      if (file.size > 2 * 1024 * 1024) {
        toast.error(t('allergen_manager.validation.file_size_limit'));
        return;
      }
      
      const previewUrl = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        iconFile: file,
        iconUrl: previewUrl,
        icon: ''
      }));
    }
  };

  // Remove Uploaded Icon
  const handleRemoveUploadedIcon = () => {
    if (formData.iconUrl) {
      URL.revokeObjectURL(formData.iconUrl);
    }
    setFormData(prev => ({
      ...prev,
      iconFile: null,
      iconUrl: ''
    }));
  };

  // Icon Options
  const iconOptions = ['🌾', '🦐', '🥚', '🐟', '🥜', '🌱', '🥛', '🌰', '🥬', '🌭', '⚫', '⚗️', '🌿', '🐚'];

  if (loading && allergens.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#7BD5B5]"></div>
        <span className="ml-3 text-gray-600">{t('allergen_manager.loading')}</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{t('allergen_manager.title')}</h2>
          <p className="text-gray-600 mt-1">{t('allergen_manager.subtitle')}</p>
        </div>
        
        {/* Two Main Buttons */}
        <div className="flex flex-col sm:flex-row flex-wrap gap-3">
          {/* Add EU Allergens Button */}
          <button
            onClick={addEUAllergens}
            disabled={loading}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[180px]"
          >
            <Globe size={18} />
            <span>{t('allergen_manager.add_eu_allergens')}</span>
          </button>
          
          {/* Create Custom Allergen Button */}
          <button
            onClick={() => setShowForm(true)}
            disabled={loading}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#7BD5B5] text-[#0A0C0B] rounded-lg hover:bg-[#6bc4a5] transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[180px]"
          >
            <Plus size={18} />
            <span>{t('allergen_manager.create_allergen_btn')}</span>
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Search and Filter */}
      <div className="bg-gray-50 border rounded-lg p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder={t('allergen_manager.search_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7BD5B5] bg-white"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
          
            <button
              onClick={fetchAllergens}
              disabled={loading}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#7BD5B5] text-[#0A0C0B] rounded-lg hover:bg-[#6bc4a5] transition-colors disabled:opacity-50"
              title={t('allergen_manager.refresh')}
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            </button>
            <span className="text-sm text-gray-500 whitespace-nowrap">
              {filteredAllergens.length} {t('allergen_manager.of_count')} {allergens.length}
            </span>
          </div>
        </div>
      </div>

      {/* Allergen Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {editingAllergen 
                  ? t('allergen_manager.edit_custom_allergen') 
                  : t('allergen_manager.create_allergen_btn')
                }
              </h2>
              <button
                onClick={resetForm}
                className="p-2 hover:bg-gray-100 rounded-lg text-2xl"
              >
                &times;
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('allergen_manager.form.name')} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 ${
                        formErrors.name ? 'border-red-500 focus:ring-red-500' : 'focus:ring-[#7BD5B5]'
                      }`}
                      placeholder={t('allergen_manager.name_placeholder_eg')}
                    />
                    {formErrors.name && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('allergen_manager.form.code_max_chars')}
                    </label>
                    <input
                      type="text"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 ${
                        formErrors.code ? 'border-red-500 focus:ring-red-500' : 'focus:ring-[#7BD5B5]'
                      }`}
                      placeholder={t('allergen_manager.code_placeholder_eg')}
                    />
                    {formErrors.code && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.code}</p>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('allergen_manager.form.description')}
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7BD5B5]"
                    placeholder={t('allergen_manager.description_placeholder_eg')}
                  />
                </div>
                
                {/* Icon Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('allergen_manager.form.icon')}
                  </label>
                  
                  {/* Upload Section */}
                  <div className="mb-4 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#7BD5B5] transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        {t('allergen_manager.form.upload_custom_icon')}
                      </span>
                      {formData.iconUrl && (
                        <button
                          type="button"
                          onClick={handleRemoveUploadedIcon}
                          className="text-red-500 hover:text-red-700 text-sm flex items-center gap-1"
                        >
                          <X size={16} />
                          {t('allergen_manager.remove')}
                        </button>
                      )}
                    </div>
                    
                    {formData.iconUrl ? (
                      <div className="flex items-center gap-3">
                        <img 
                          src={formData.iconUrl} 
                          alt={t('allergen_manager.icon_preview')} 
                          className="w-12 h-12 object-contain border rounded"
                        />
                        <span className="text-sm text-gray-600">{formData.iconFile?.name}</span>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center cursor-pointer group">
                        <div className="text-center">
                          <Upload className="mx-auto h-8 w-8 text-gray-400 group-hover:text-[#7BD5B5] mb-2" />
                          <span className="text-sm text-gray-600">{t('allergen_manager.form.click_to_upload')}</span>
                          <span className="text-xs text-gray-500 block mt-1">{t('allergen_manager.form.file_types')}</span>
                        </div>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                          onChange={handleIconUpload}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-1 border-t border-gray-300"></div>
                    <span className="text-sm text-gray-500">{t('allergen_manager.or_separator')}</span>
                    <div className="flex-1 border-t border-gray-300"></div>
                  </div>
                  
                  {/* Saved SVG icons (14 EU allergens) */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('allergen_manager.form.choose_from_saved_svgs', 'Scegli icona dagli allergeni salvati (SVG)')}
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {PUBLIC_ALLERGENS.map((a) => (
                        <button
                          key={a.code}
                          type="button"
                          onClick={() => {
                            handleRemoveUploadedIcon();
                            setFormData(prev => ({ ...prev, icon: a.icon, name: prev.name || a.nameIt, code: prev.code || a.code }));
                          }}
                          className={`flex flex-col items-center p-2 rounded-lg border transition-colors ${
                            formData.icon === a.icon && !formData.iconUrl
                              ? 'border-[#7BD5B5] bg-[#7BD5B5]/10'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                          title={a.nameIt}
                        >
                          <img src={a.icon} alt={a.nameIt} className="w-6 h-6 object-contain" />
                          <span className="text-xs mt-0.5 text-gray-600">{a.code}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                
                </div>
              </div>
              
              {/* Form Actions */}
              <div className="flex flex-col sm:flex-row sm:justify-end gap-3 mt-6 pt-6 border-t">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors w-full sm:w-auto"
                >
                  {t('allergen_manager.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2.5 bg-[#7BD5B5] text-[#0A0C0B] rounded-lg hover:bg-[#6bc4a5] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 w-full sm:w-auto"
                >
                  {saving && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0A0C0B]"></div>
                  )}
                  {editingAllergen 
                    ? t('allergen_manager.update_allergen') 
                    : t('allergen_manager.create_allergen')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Allergens List */}
      {filteredAllergens.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-xl">
          {allergens.length === 0 ? (
            <>
              <div className="flex justify-center gap-4 mb-6">
                <Plus className="h-12 w-12 text-[#7BD5B5]" />
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                {t('allergen_manager.messages.no_allergens_yet')}
              </h3>
              <p className="mt-2 text-gray-500">
                {t('allergen_manager.messages.get_started')}
              </p>
             
            </>
          ) : (
            <>
              <AlertCircle className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                {t('allergen_manager.messages.no_matching_allergens')}
              </h3>
              <p className="mt-2 text-gray-500">
                {t('allergen_manager.messages.adjust_search')}
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredAllergens.map(allergen => (
            <div
              key={allergen.id}
              className={`bg-white border rounded-lg p-4 transition-all hover:shadow-md ${
                !allergen.isActive ? 'opacity-60 bg-gray-50' : ''
              }`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  {(() => {
                    const iconSrc = getAllergenIconForDisplay(allergen);
                    const displayName = getAllergenDisplayNameItalian({ id: allergen.id, name: allergen.name, code: allergen.code, icon: allergen.icon });
                    return iconSrc ? (
                      <span className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                        {isIconImage(iconSrc) ? (
                          <img src={iconSrc} alt={displayName} className="w-8 h-8 object-contain" />
                        ) : (
                          <span className="text-2xl">{iconSrc}</span>
                        )}
                      </span>
                    ) : null;
                  })()}
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-lg truncate">
                      {getAllergenDisplayNameItalian({ id: allergen.id, name: allergen.name, code: allergen.code, icon: allergen.icon })}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">
                        {t('allergen_manager.code_label')}: {allergen.code}
                      </span>
                      {allergen.isEUStandard && (
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                          {t('allergen_manager.eu_standard')}
                        </span>
                      )}
                      <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${
                        allergen.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {allergen.isActive ? (
                          <>
                            <Check size={12} />
                            {t('allergen_manager.status.active')}
                          </>
                        ) : (
                          <>
                            <X size={12} />
                            {t('allergen_manager.status.inactive')}
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Status Toggle Button */}
                  {!allergen.isActive ? (
                    <button
                      onClick={() => handleReactivate(allergen)}
                      className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                      title={t('allergen_manager.reactivate_title')}
                    >
                      <Eye size={18} />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleDeactivate(allergen)}
                      className="p-2 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50 rounded-lg transition-colors"
                      title={t('allergen_manager.deactivate_title')}
                    >
                      <EyeOff size={18} />
                    </button>
                  )}
                  
                  {/* Edit Button */}
                  <button
                    onClick={() => handleEdit(allergen)}
                    className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                    title={t('allergen_manager.edit_title')}
                  >
                    <Edit size={18} />
                  </button>
                  
                  {/* Permanent Delete Button - ALWAYS VISIBLE */}
                  <button
                    onClick={() => handlePermanentDelete(allergen)}
                    className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                    title={t('allergen_manager.permanent_delete_title')}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              
              {(() => {
                const desc = getAllergenDescriptionItalian(allergen);
                return desc ? (
                  <p className="text-gray-600 text-sm mt-2 line-clamp-2">{desc}</p>
                ) : null;
              })()}
              
              <div className="mt-4 pt-3 border-t text-xs text-gray-500 flex justify-between">
                <span>ID: {allergen.id}</span>
                {allergen.isEUStandard ? (
                  <span className="text-blue-600">{t('allergen_manager.eu_standard_allergen')}</span>
                ) : (
                  <span className="text-green-600">{t('allergen_manager.custom_allergen')}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

     
    </div>
  );
};

export default AllergenManager;